/**
 * The online lobby of the game client (design D5, client side), on the screens of the original with minimal changes:
 *  - the mode board of the fight screen ("Режим игры", menu_fight) has a sixth mode "Онлайн" (the 'online' button, an
 *    asset patch next to career / missions / survival / boss / just play): the fight screen in that mode is the lobby,
 *    laid out like the other modes. The task list on the right lists the open rooms of the server (room.list, page by
 *    page with the `next` cursor: "show more rooms"; refreshed every 5 s, the pages loaded so far included); a click
 *    picks a room (kept across the refreshes; one that fills up or leaves the list is no longer picked and the lobby
 *    says so, never swapped for another room), the battle button ("ИГРАТЬ!!!") joins it. The left side shows the
 *    picked room's map and teams where a task shows its map and teams,
 *    and below them, apart from the list, the player's own actions under their headings: create an open or a private
 *    room (the yellow buttons of the original) and join a private room by its code (a field and the green button);
 *  - inside a room the "Просто играть" barracks screen (menu_just_play) shows the room's RoomSettings: the map (any
 *    PvP map for every host: a room is its own mode without the career lock of the offline screen; the map list is
 *    RoomMapList, src/game/runtime/online-room-maps.ts), the teams and their worms, the players with their ready flags
 *    and the room code; its mode board shows "Онлайн" as the chosen mode; the host edits the settings (room.update), the
 *    others see them read-only (the room's map only); the battle button (go_just) toggles Ready for a guest and, for the
 *    host once everyone is ready, readies the host and starts the battle (room.start); the close button leaves the room
 *    (room.leave) back to the lobby;
 *  - room.updated / room.closed / session.started arrive as server events and are applied in the game loop (a queue,
 *    never the blocking NET_INIT bridge);
 *  - session.started starts the online battle (OnlineMatch, src/game/runtime/online-match.ts); its end shows the result
 *    (menu_question, mode 'online_result': the winner team, the server's verdict when it arrives) and goes back to the
 *    room, or to the lobby when the room is gone;
 *  - after a page reload the client finds its running battle again (the session id kept client-side, session.get) and
 *    rebuilds it from the server's log (a fast-forward), and its room (room.get).
 * The offline hot-seat "Просто играть" (the fight screen's init_just_play) is untouched: the barracks screen is in room
 * mode only when this module opened it for the room (roomLink).
 *
 * Browser-safe: no node imports; the protocol shapes are read structurally.
 */
import { ColorTransform, KeyboardEvent, Point, Rectangle, Shape, TextField, TextFormat, type DisplayObject } from '@flash';
import type { LockstepCoordinator } from '../../session/lockstep';
import { DEFAULT_ONLINE_MAP, onlineMap, onlineMapOf } from '../../session/online';
import { OnlineMatch, ONLINE_FLUSH_EVERY_TICKS, type OnlineSession } from './online-match';
import { RoomMapList, onlineMinimap } from './online-room-maps';
import { sessionEnded } from './net-policy';
import * as T from './online-texts';
import { playerName, translate } from './i18n';
import { ERROR_COLOR, HINT_COLOR, LABEL_COLOR, inputField, label, setRawText, setRawTextFit } from '../ui/controls';
import { listPitch } from '../ui/wide-lists';

/** A lobby room as the game reads it (Room of src/shared/protocol/model.ts). */
export interface OnlineRoom {
  roomId: string;
  code: string | null;
  hostId: string;
  private: boolean;
  /** The map by both fields (the stable mapId and its "Карта №N" number) and the teams. */
  settings: { mapId: string; mapIndex: number; teams: { worms: number; color: number; name: string }[] };
  players: { playerId: string; displayName: string; team: number; ready: boolean; connected: boolean }[];
  maxPlayers: number;
  state: 'lobby' | 'in_game' | 'closed';
  sessionId: string | null;
  version: number;
}

/** The settings the lobby sends (room.create / room.update): the map by its stable id. */
type SentSettings = { mapId: string; teams: OnlineRoom['settings']['teams'] };

/** Client-side memory of the player's room and running battle (the "fwp_prefs" SharedObject: localStorage). */
interface Stored {
  playerId: string;
  sessionId: string | null;
  roomId: string | null;
}

const PREF_KEY = 'online';
/** The lobby list is refreshed this often while it is on screen. */
const LIST_REFRESH_MS = 5000;
/** room.list's page sizes (its default and largest `limit`; the game never imports the wire protocol). */
const LIST_PAGE = 20;
const LIST_PAGE_MAX = 100;
const MAX_WORMS = 12;
/** Positions of the unnamed static texts this module rewrites (menu_fight: the task list's heading; menu_just_play: the description). */
const LIST_TITLE = { x: 443, y: 179 };
const JUST_PLAY_INFO = { x: 101, y: 337 };
/**
 * The fight screen's task list (choose_container, its mask; new_interf init_instant): rows of 20 px, 16 shown (on a
 * phone the rows are listPitch apart, ui/wide-lists.ts: as many as 16 rows of 20 px hold).
 */
const LIST_TOP = 204;
const LIST_ROW = 20;
const LIST_ROWS = 16;
/**
 * The player's actions on the left of the fight screen, where a task shows its description (fight_text, 100..375 x
 * 288..590): the x range, the two button columns and the rows (headings, buttons, the separating line, the status).
 */
const ACTIONS = { x: 100, width: 275, left: 101, right: 244, createTitle: 290, createHint: 308, createButtons: 344, separator: 395.5, joinTitle: 402, joinButton: 427, status: 482 };
/** The children build_fight adds for the actions (hidden when the server does not offer online play). */
const ACTION_NAMES = [
  'online_create_title',
  'online_create_hint',
  'online_create',
  'online_create_label',
  'online_create_private',
  'online_create_private_label',
  'online_separator',
  'online_join_title',
  'online_code',
  'online_join_code',
  'online_join_code_label',
];
/** The 145 x 40 buttons of the original at this scale fit two in a row. */
const ACTION_BUTTON_SCALE = 0.9;
/** Colours: the headings of the fight screen ("Режим игры"), the line, the notes of the list, the chosen task. */
const HEADING_COLOR = 0x0066ff;
const SEPARATOR_COLOR = 0x9c8f5a;
const NOTE_COLOR = 0x666666;
const CHOSEN_COLOR = 16711680;
/** The alpha of the other modes' cards on the mode board of a room (not available while in the room). */
const OTHER_MODE_ALPHA = 0.35;

/** A room with no free place (room.list lists them too). */
function roomFull(r: OnlineRoom): boolean {
  return r.players.length >= r.maxPlayers;
}

/** The unnamed text field of `link` at (x, y) (a static text of the screen). */
function textAt(link: any, at: { x: number; y: number }): any | null {
  for (let k = 0; link && k < link.numChildren; k++) {
    const c = link.getChildAt(k);
    if (c instanceof TextField && Math.abs(c.x - at.x) < 4 && Math.abs(c.y - at.y) < 4) return c;
  }
  return null;
}

function codeOf(err: any): string {
  return err && typeof err.code === 'string' ? err.code : 'UNAVAILABLE';
}

export class OnlineLobby {
  /** The first page (and the pages loaded after it) of the public rooms. */
  rooms: OnlineRoom[] = [];
  next: string | null = null;
  listLoaded = false;
  listLoading = false;
  /** The room this player is in (null: none). */
  room: OnlineRoom | null = null;
  /** The status line of the room screen (why the battle cannot start yet, an error). */
  message: string | null = null;
  /** A request of the lobby is in flight (clicks wait for it). */
  busy = false;
  match: OnlineMatch | null = null;
  /** A session.started that waits for the game to be idle. */
  pendingStart: { session: OnlineSession; coordinator: LockstepCoordinator } | null = null;
  /** The barracks screen opened in room mode (its display object). */
  roomLink: any = null;
  /** The fight screen the lobby's controls were built on (its display object). */
  fightLink: any = null;
  /** The picked room of the list (kept by id across the list's refreshes and pages; null: none). */
  selectedRoomId: string | null = null;
  /**
   * The player picked a room with a click (false: the lobby picks the first room with a free place, as the task list
   * always has a chosen task). The player's pick is never swapped for another room: when it fills up or leaves the
   * list, nothing is picked and the lobby says why (keepPick).
   */
  playerPicked = false;
  /** The room screen's map list. */
  readonly maps: RoomMapList;
  resultShown = false;
  /** Diagnostics (tests). */
  readonly trace: string[] = [];

  private readonly tasks: (() => void)[] = [];
  private attached = false;
  private offs: (() => void)[] = [];
  private listAt = -Infinity;
  private openingRoom = false;
  private resumeChecked = false;
  private resultVersion = -1;

  constructor(readonly fwp: any) {
    this.maps = new RoomMapList(fwp);
  }

  get services(): any {
    return this.fwp.services;
  }

  get playerId(): string {
    return String(this.fwp.user.player_id ?? '');
  }

  /** The backend offers rooms and lockstep sessions (test doubles without them: the tab says so). */
  get available(): boolean {
    const s = this.services;
    return !!s && typeof s.createRoom === 'function' && typeof s.listRoomsPage === 'function' && typeof s.createLockstep === 'function' && typeof s.on === 'function';
  }

  private log(line: string): void {
    this.trace.push(line);
    if (this.trace.length > 200) this.trace.splice(0, this.trace.length - 200);
  }

  private queue(fn: () => void): void {
    this.tasks.push(fn);
  }

  /** Subscribes to the server's room and session events (once). */
  attach(): void {
    if (this.attached || !this.available) return;
    this.attached = true;
    const s = this.services;
    this.offs.push(
      s.on('room.updated', (e: any) => this.queue(() => this.onRoomUpdated(e.room))),
      s.on('room.closed', (e: any) => this.queue(() => this.onRoomClosed(e.roomId))),
      s.on('session.started', (e: any) => this.onSessionStarted(e.session)),
    );
  }

  /** Stops listening to the server and drops the lobby's pending battle and running match (tools and test harnesses). */
  dispose(): void {
    for (const off of this.offs) off();
    this.offs = [];
    this.attached = true; // never again
    this.pendingStart?.coordinator.dispose();
    this.pendingStart = null;
    this.match?.close();
    this.match = null;
  }

  // ------------------------------------------------------------------------------------------------ game loop

  /** Every frame of FWP._main (not while a blocking call runs); true when the online battle ran this frame. */
  frame(): boolean {
    this.attach();
    for (let n = this.tasks.length; n > 0 && this.tasks.length > 0; n--) this.tasks.shift()!();
    const f = this.fwp;
    if (!this.resumeChecked && this.available && this.uiIdle()) {
      this.resumeChecked = true;
      this.checkStored();
    }
    if (this.pendingStart && this.canStartBattle()) this.startMatch();
    const owned = this.match ? this.match.frame() : false;
    const m = this.match;
    if (m && m.phase === 'ended' && !this.resultShown && this.uiIdle() && !f._proc) this.showResult();
    if (m && this.resultShown && m.version !== this.resultVersion && this.isResultScreen() && !f.animation) {
      this.resultVersion = m.version;
      f._interf.render();
    }
    if (this.isLobbyScreen() && !this.listLoading && Number(f.net_now()) - this.listAt > LIST_REFRESH_MS) this.loadRooms(true);
    return owned;
  }

  /** The interface is idle in the menus (no call, animation or battle). */
  private uiIdle(): boolean {
    const f = this.fwp;
    return f.main_mode == 'IDLE' && !f.net_pending && !f.animation && !f.execute_after_animation && !f.grave_searching && !f.main_map_dl && !f.loading_screen && !!f._interf && !f.OOPS_BANNED;
  }

  private canStartBattle(): boolean {
    const f = this.fwp;
    return this.uiIdle() && !f._proc && !f.REPLAY;
  }

  private top(): { name: string; link: any } | null {
    const i = this.fwp._interf;
    return i && i.cur_screen && i.cur_screen[0] ? i.cur_screen[i.cur_screen[0]] : null;
  }

  isLobbyScreen(): boolean {
    return this.top()?.name == 'fight' && this.fwp._interf.play_type == 'online';
  }

  isRoomScreen(): boolean {
    const t = this.top();
    return !!t && t.name == 'just_play' && !!this.room && t.link === this.roomLink;
  }

  isResultScreen(): boolean {
    const t = this.top();
    return !!t && t.name == 'question' && this.fwp._interf.question_mode == 'online_result';
  }

  private notice(text: string): void {
    this.fwp.net_notice(text, 4000);
  }

  // ------------------------------------------------------------------------------------------------ client memory

  private stored(): Stored | null {
    const v = this.fwp.pref(PREF_KEY);
    return v && typeof v === 'object' && v.playerId === this.playerId ? (v as Stored) : null;
  }

  private store(patch: Partial<Stored>): void {
    const cur = this.stored() ?? { playerId: this.playerId, sessionId: null, roomId: null };
    this.fwp.pref(PREF_KEY, { ...cur, ...patch, playerId: this.playerId });
  }

  /** After a (re)load: the battle this player was playing (rebuilt from the server's log) and the room it was in. */
  private checkStored(): void {
    const s = this.stored();
    if (!s) return;
    const services = this.services;
    if (s.sessionId) {
      const id = s.sessionId;
      services.getSession(id).then(
        (r: any) => {
          const info = r.session as OnlineSession;
          const me = info.participants.find((p) => p.playerId === this.playerId);
          if (info.state !== 'active' || !info.online || !me || me.forfeited) {
            this.queue(() => this.store({ sessionId: null }));
            return;
          }
          services.resumeLockstep(id, { flushEveryTicks: ONLINE_FLUSH_EVERY_TICKS }).then(
            (c: LockstepCoordinator) =>
              this.queue(() => {
                if (this.match || this.pendingStart) return c.dispose();
                this.log(`resuming session ${id} from the log (turn ${c.turn}, through tick ${c.log.current.throughTick})`);
                this.pendingStart = { session: info, coordinator: c };
              }),
            (err: any) => this.log(`resume failed: ${codeOf(err)}`),
          );
        },
        (err: any) => this.queue(() => (codeOf(err) === 'NOT_FOUND' || codeOf(err) === 'FORBIDDEN' ? this.store({ sessionId: null }) : undefined)),
      );
    }
    if (s.roomId) {
      services.getRoom(s.roomId).then(
        (room: OnlineRoom) => this.queue(() => (!this.room && room.players.some((p) => p.playerId === this.playerId) ? this.adoptRoom(room) : undefined)),
        (err: any) => this.queue(() => (codeOf(err) === 'NOT_FOUND' || codeOf(err) === 'FORBIDDEN' ? this.store({ roomId: null }) : undefined)),
      );
    }
  }

  // ------------------------------------------------------------------------------------------------ server events

  private onRoomUpdated(room: OnlineRoom): void {
    if (!room) return;
    const mine = room.players.some((p) => p.playerId === this.playerId);
    if (this.room && room.roomId === this.room.roomId) {
      if (room.version < this.room.version) return;
      if (!mine) return this.lostRoom(null);
      this.adoptRoom(room);
    } else if (!this.room && mine && room.state !== 'closed') this.adoptRoom(room);
  }

  private onRoomClosed(roomId: string): void {
    if (this.room && this.room.roomId === roomId) this.lostRoom(T.MSG_ROOM_CLOSED);
  }

  /** The player is no longer in its room: back to the lobby when the room was on screen. */
  private lostRoom(text: string | null): void {
    const onScreen = this.isRoomScreen();
    // A closed room that was the player's pick in the lobby is no longer picked: the notice here says it closed, the
    // lobby's next refresh says nothing more about it (and picks the first free room again).
    if (text && this.room && this.room.roomId === this.selectedRoomId) {
      this.selectedRoomId = null;
      this.playerPicked = false;
    }
    this.room = null;
    this.store({ roomId: null });
    if (text) this.notice(text);
    if (onScreen) this.openLobbyScreen();
  }

  private adoptRoom(room: OnlineRoom): void {
    if (this.room && this.room.roomId === room.roomId && room.version !== this.room.version) this.message = null;
    this.room = room;
    if (this.stored()?.roomId !== room.roomId) this.store({ roomId: room.roomId });
    if (this.isRoomScreen()) this.fwp._interf.render();
  }

  /**
   * session.started (or the host's room.start answer): the coordinator subscribes at once (no relayed input of the
   * battle is missed while the game gets ready), the battle starts once the game is idle.
   */
  private onSessionStarted(session: OnlineSession): void {
    if (!session || !session.online || !session.participants.some((p) => p.playerId === this.playerId)) return;
    if (this.match && this.match.sessionId === session.sessionId) return;
    if (this.pendingStart && this.pendingStart.session.sessionId === session.sessionId) return;
    this.log(`session ${session.sessionId} started`);
    if (session.turn === 0) {
      try {
        const c = this.services.createLockstep(session, { flushEveryTicks: ONLINE_FLUSH_EVERY_TICKS });
        this.pendingStart?.coordinator.dispose();
        this.pendingStart = { session, coordinator: c };
        return;
      } catch (err: any) {
        this.log(`createLockstep: ${err?.message ?? err}`);
      }
    }
    // Already under way (a late event): the log so far comes with the coordinator.
    this.services.resumeLockstep(session.sessionId, { flushEveryTicks: ONLINE_FLUSH_EVERY_TICKS }).then(
      (c: LockstepCoordinator) =>
        this.queue(() => {
          if ((this.match && this.match.sessionId === session.sessionId) || this.pendingStart) return c.dispose();
          this.pendingStart = { session, coordinator: c };
        }),
      (err: any) => this.log(`resumeLockstep: ${codeOf(err)}`),
    );
  }

  /** Closes the popups on top of the screen stack (a battle starts from the menus; popups would block remove_all). */
  private closePopups(): void {
    const i = this.fwp._interf;
    for (let guard = 0; i.cur_screen[0] > 0 && guard < 20; guard++) {
      const name = i.cur_screen[i.cur_screen[0]].name;
      if (name in i.no_anim_def) break;
      i.remove_instant();
    }
  }

  private startMatch(): void {
    const { session, coordinator } = this.pendingStart!;
    this.pendingStart = null;
    if (this.match) {
      this.match.close();
      this.match = null;
    }
    this.resultShown = false;
    this.message = null;
    this.closePopups();
    const m = new OnlineMatch(this.fwp, session, coordinator, this.playerId);
    this.match = m;
    this.store({ sessionId: session.sessionId, ...(session.roomId ? { roomId: session.roomId } : {}) });
    try {
      m.launch();
      this.log(`battle of session ${session.sessionId} launched`);
    } catch (err: any) {
      this.log(`launch failed: ${err?.message ?? err}`);
      m.close();
      this.match = null;
      this.notice(T.MSG_ONLINE_FAILED);
    }
  }

  // ------------------------------------------------------------------------------------------------ result

  private showResult(): void {
    const m = this.match!;
    this.resultShown = true;
    this.resultVersion = m.version;
    this.closePopups();
    // Back in the room (its barracks screen) with the result on top; without a room it shows over the menus and
    // closing it goes to the lobby.
    if (this.room) this.openRoomScreen();
    this.fwp._interf.init('question', 'online_result');
  }

  private closeResult(): void {
    const m = this.match;
    this.match = null;
    this.resultShown = false;
    if (m) m.close();
    this.store({ sessionId: null });
    // Back in the room under the result; without a room (it closed during the battle) the lobby replaces the screens.
    if (this.room) this.fwp._interf.remove();
    else this.openLobbyScreen();
  }

  /** menu_question in mode 'online_result': the winner and the server's verdict. */
  render_result(link: any): void {
    const m = this.match;
    if (!m) {
      link.header.text = T.MSG_BATTLE_OVER;
      link.main.text = '';
      return;
    }
    const winner = m.winner;
    link.header.text = winner !== null && winner > 0 ? (m.localTeams.includes(winner) ? T.MSG_WIN : T.MSG_LOSE) : T.MSG_BATTLE_OVER;
    const lines: string[] = [];
    if (m.end === 'forfeited') lines.push(T.MSG_YOU_SURRENDERED);
    if (m.end === 'opponent_left') lines.push(T.MSG_OPPONENT_LEFT);
    if (m.end === 'desync') lines.push(T.MSG_DESYNC);
    if (m.end === 'lost') lines.push(T.MSG_BATTLE_LOST);
    // A dispute decided by the server's simulation: whose game went out of sync.
    const diverged = m.outcome && m.outcome.diverged;
    if (diverged && diverged.length) lines.push(diverged.includes(m.playerId) ? T.MSG_DESYNC_YOURS : T.MSG_DESYNC_THEIRS);
    if (winner !== null && (m.outcome || m.end !== 'desync')) lines.push(winner > 0 ? T.MSG_WINNER + m.teamLabel(winner) + '.' : T.MSG_NO_WINNER);
    if (m.outcome) lines.push(m.outcome.verdict === 'verified' ? T.MSG_VERDICT_OK : m.outcome.verdict === 'rejected' ? T.MSG_VERDICT_REJECTED : T.MSG_VERDICT_UNVERIFIED);
    else if ((m.end === 'finished' && m.finishStatus !== 'failed') || m.end === 'desync') lines.push(T.MSG_VERDICT_WAIT);
    link.main.text = lines.join(' ');
    link.proceed.visible = true;
  }

  // ------------------------------------------------------------------------------------------------ lobby screen

  /** The fight screen was opened in the "Онлайн" mode (new_interf init_instant): the lobby. */
  enter(): void {
    this.attach();
    this.listLoaded = false;
    this.message = null;
    // A pick that was lost (and said so) is not kept over a new visit: the lobby picks the first free room again.
    if (this.selectedRoomId === null) this.playerPicked = false;
    if (this.available) {
      // A player who is in a room goes straight back to it.
      if (this.room) this.queue(() => (this.isLobbyScreen() && this.room ? this.openRoomScreen() : undefined));
      this.loadRooms(true);
    }
  }

  /**
   * The room list from the server: a refresh (`reset`) reads the list again from its start, as many rooms as the
   * player has loaded ("show more rooms" pages included, so a room picked on a later page stays listed); otherwise the
   * next page after the loaded ones. A picked room the refresh no longer reaches is asked for by itself (pickedRoom):
   * the list may have moved it past the loaded rooms (an older room whose battle ended is back in the lobby ahead of
   * it) while it is still open, and then it stays listed after the loaded rooms.
   */
  private loadRooms(reset: boolean): void {
    if (this.listLoading || !this.available) return;
    this.listLoading = true;
    this.listAt = Number(this.fwp.net_now());
    const load: Promise<{ rooms: OnlineRoom[]; next: string | null; picked?: OnlineRoom | null }> = reset
      ? this.firstRooms(Math.max(this.rooms.length, LIST_PAGE)).then((r) => this.pickedRoom(r))
      : this.services.listRoomsPage(this.next ? { cursor: this.next } : {});
    load.then(
      (r: { rooms: OnlineRoom[]; next: string | null; picked?: OnlineRoom | null }) =>
        this.queue(() => {
          this.listLoading = false;
          this.listLoaded = true;
          if (reset) {
            this.rooms = r.rooms;
            // The picked room, still in the lobby, after the rooms the refresh read (while it is still the pick).
            const p = r.picked;
            if (p && p.roomId === this.selectedRoomId && !this.rooms.some((x) => x.roomId === p.roomId)) this.rooms = [...this.rooms, p];
          } else {
            // A page of "show more rooms" may hold the picked room already listed after the loaded ones.
            const listed = new Set(this.rooms.map((x) => x.roomId));
            this.rooms = [...this.rooms, ...r.rooms.filter((x) => !listed.has(x.roomId))];
          }
          this.next = r.next;
          this.keepPick();
          if (this.isLobbyScreen()) this.fwp._interf.render();
        }),
      () =>
        this.queue(() => {
          this.listLoading = false;
          this.listLoaded = true;
          if (this.isLobbyScreen()) this.fwp._interf.render();
        }),
    );
  }

  /** The first `count` rooms of the list (room.list page after page: a page holds at most LIST_PAGE_MAX rooms). */
  private async firstRooms(count: number): Promise<{ rooms: OnlineRoom[]; next: string | null }> {
    const rooms: OnlineRoom[] = [];
    let next: string | null = null;
    do {
      const limit = Math.min(count - rooms.length, LIST_PAGE_MAX);
      const r: { rooms: OnlineRoom[]; next: string | null } = await this.services.listRoomsPage(next ? { limit, cursor: next } : { limit });
      rooms.push(...r.rooms);
      next = r.next;
      if (r.rooms.length === 0) break;
    } while (next && rooms.length < count);
    return { rooms, next };
  }

  /**
   * A refreshed list without the room the player picked: the room itself (room.get), when it is still in the lobby
   * (full or not: keepPick says which). A room that closed, began its battle or cannot be read is left out, and
   * keepPick drops the pick as gone.
   */
  private async pickedRoom(r: { rooms: OnlineRoom[]; next: string | null }): Promise<{ rooms: OnlineRoom[]; next: string | null; picked: OnlineRoom | null }> {
    const id = this.selectedRoomId;
    if (!this.playerPicked || id === null || r.rooms.some((x) => x.roomId === id)) return { ...r, picked: null };
    try {
      const room = await this.services.getRoom(id);
      return { ...r, picked: room && room.roomId === id && room.state === 'lobby' ? room : null };
    } catch {
      return { ...r, picked: null };
    }
  }

  /** The lobby: the fight screen in the "Онлайн" mode (a tab screen: it replaces the screens under it). */
  private openLobbyScreen(): void {
    const i = this.fwp._interf;
    this.closePopups();
    i.remove_all();
    i.init('fight', 'online');
  }

  /** The picked room of the list, while it is listed. */
  selectedRoom(): OnlineRoom | null {
    return this.rooms.find((r) => r.roomId === this.selectedRoomId) ?? null;
  }

  /**
   * After the list changed: the room the player picked stays picked while it is listed with a free place. One that
   * filled up or left the list (closed, its battle began) is no longer picked and the lobby says so (the status line
   * and a notice); no other room is picked in its place, so the battle button never leads elsewhere than the player
   * chose. Only on the lobby screen out of a room: a list that answers once the player joined the picked room (full
   * with the player in it) or left the lobby says nothing; the lobby's next refresh judges the pick again.
   */
  private keepPick(): void {
    if (!this.playerPicked || this.selectedRoomId === null || this.room || !this.isLobbyScreen()) return;
    const r = this.selectedRoom();
    if (r && !roomFull(r)) return;
    this.selectedRoomId = null;
    this.message = r ? T.MSG_PICKED_ROOM_FULL : T.MSG_PICKED_ROOM_GONE;
    this.notice(this.message);
  }

  /**
   * The lobby's own controls on the fight screen (new_interf init_instant, before the screen's children get their
   * listeners): below the picked room's teams, where a task shows its description, the player's actions under their
   * headings - create an open or a private room (the yellow buttons of the original), a line, join a private room by
   * its code (a field and the green button) - and the status line.
   */
  build_fight(link: any): void {
    this.fightLink = link;
    link.fight_text.visible = false;
    const add = <D extends DisplayObject>(o: D, name: string, x: number, y: number): D => {
      o.name = name;
      o.x = x;
      o.y = y;
      link.addChild(o);
      return o;
    };
    const heading = (text: string) => label(text, { size: 12, bold: true, color: HEADING_COLOR, align: 'center', width: ACTIONS.width });
    add(heading(T.MSG_CREATE_OWN_ROOM), 'online_create_title', ACTIONS.x, ACTIONS.createTitle);
    add(label(T.MSG_CREATE_ROOM_HINT, { size: 11, align: 'center', width: ACTIONS.width, lines: 2 }), 'online_create_hint', ACTIONS.x, ACTIONS.createHint);
    this.actionButton(link, 'btn_yellow', 'online_create', T.MSG_CREATE_OPEN, ACTIONS.left, ACTIONS.createButtons);
    this.actionButton(link, 'btn_yellow', 'online_create_private', T.MSG_CREATE_PRIVATE, ACTIONS.right, ACTIONS.createButtons);
    const line = add(new Shape(), 'online_separator', 0, 0);
    line.graphics.lineStyle(1, SEPARATOR_COLOR, 1);
    line.graphics.moveTo(ACTIONS.x + 8, ACTIONS.separator);
    line.graphics.lineTo(ACTIONS.x + ACTIONS.width - 7, ACTIONS.separator);
    add(heading(T.MSG_JOIN_PRIVATE), 'online_join_title', ACTIONS.x, ACTIONS.joinTitle);
    const code = add(inputField('online_code', { width: 128, maxChars: 12, restrict: 'A-Za-z0-9' }), 'online_code', ACTIONS.left + 1, ACTIONS.joinButton + 7);
    this.actionButton(link, 'btn_green', 'online_join_code', T.MSG_JOIN_BY_CODE, ACTIONS.right, ACTIONS.joinButton);
    // Enter (the phone keyboard's "go") joins as the button beside the field does; above an on-screen keyboard the
    // view shows the field with that button (keyboardWith: src/flash/platform/browser-host.ts keyboardRect).
    code.addEventListener(KeyboardEvent.KEY_DOWN, (e: any) => {
      if (e.keyCode === 13 && this.isLobbyScreen()) this.joinByCode(link);
    });
    code.$keyboardWith = 'online_join_code';
    add(label('', { size: 11, color: HINT_COLOR, width: ACTIONS.width, lines: 4 }), 'online_status', ACTIONS.x, ACTIONS.status);
  }

  /** A button of the original (a linkage class of the asset patches) at 0.9x with its label, like the SWF places theirs. */
  private actionButton(link: any, cls: string, name: string, text: string, x: number, y: number): void {
    const b = this.fwp.container_interf.get_object(cls);
    b.name = name;
    b.scaleX = b.scaleY = ACTION_BUTTON_SCALE;
    link.addChild(b);
    const r = b.getBounds(link);
    b.x = x - r.x;
    b.y = y - r.y;
    const caption = label(text, { size: 12, bold: true, color: LABEL_COLOR, align: 'center', width: r.width });
    // A longer translation gets a smaller font until it fits the button (as the port's small buttons do).
    for (let size = 11; caption.textWidth > r.width - 8 && size >= 9; size--) {
      const fmt = caption.defaultTextFormat;
      fmt.size = size;
      caption.defaultTextFormat = fmt;
      caption.text = text;
    }
    caption.name = name + '_label';
    caption.x = x;
    caption.y = Math.round(y + (r.height - caption.textHeight) / 2 - 2);
    link.addChild(caption);
  }

  /** The fight screen in the "Онлайн" mode (new_interf.render): the room list, the picked room, the status line. */
  render_fight(link: any): void {
    const f = this.fwp;
    if (link !== this.fightLink) this.build_fight(link);
    // The list on the right, under the task list's heading.
    const title = textAt(link, LIST_TITLE);
    if (title) title.htmlText = '<p align="center"><font face="Arial" size="11" color="#000000"><b>' + T.MSG_OPEN_ROOMS + ' (' + this.rooms.length + (this.next ? '+' : '') + ')</b></font></p>';
    // As the task list always has a chosen task, the first room with a free place is picked until the player picks one;
    // the player's pick is kept (keepPick), never swapped for another room.
    const picked0 = this.selectedRoom();
    if (!this.playerPicked && (!picked0 || roomFull(picked0))) this.selectedRoomId = this.rooms.find((r) => !roomFull(r))?.roomId ?? null;
    const box = link.choose_container;
    while (box.numChildren > 0) box.removeChildAt(0);
    const pitch = listPitch(link.stage);
    const row = (name: string): any => {
      const e = box.addChild(f.container_interf.get_object('choose_task'));
      e.name = name;
      e.x = 0;
      e.y = (box.numChildren - 1) * pitch;
      return e;
    };
    const note = (name: string, text: string, clickable: boolean) => {
      const e = row(name);
      e.task_name.htmlText = '<i>' + text + '</i>';
      e.task_name.textColor = NOTE_COLOR;
      if (!clickable) e.mouseEnabled = e.mouseChildren = false;
    };
    if (!this.available) note('online_info', T.MSG_ONLINE_UNAVAILABLE, false);
    else {
      if (!this.listLoaded && this.rooms.length === 0) note('online_info', T.MSG_ROOMS_LOADING, false);
      else if (this.rooms.length === 0) note('online_info', T.MSG_NO_ROOMS, false);
      this.rooms.forEach((r, k) => {
        const e = row('room_' + k);
        const host = r.players.find((p) => p.playerId === r.hostId) ?? r.players[0];
        const mapIndex = (onlineMapOf(r.settings) ?? DEFAULT_ONLINE_MAP).mapIndex;
        // Display text: the game's words translated, the host's name as typed; the map in bold, as a task's name.
        const hostName = host ? playerName(host.displayName) : '?';
        setRawTextFit(e.task_name, T.roomRowText(hostName, mapIndex, r.players.length, r.maxPlayers, roomFull(r)), hostName);
        const bold = new TextFormat();
        bold.bold = true;
        e.task_name.setTextFormat(bold, 0, T.roomMapText(mapIndex).length);
        e.task_name.textColor = r.roomId === this.selectedRoomId ? CHOSEN_COLOR : roomFull(r) ? NOTE_COLOR : 0;
      });
      if (this.next) note('online_more', T.MSG_MORE_ROOMS, true);
    }
    this.layoutList(link, box.numChildren);
    // The battle button joins the picked room.
    const picked = this.selectedRoom();
    link.go.visible = !!picked && !roomFull(picked);
    this.renderPreview(link, picked);
    // The player's actions (not offered when the server does not offer online play) and the status line.
    for (const name of ACTION_NAMES) {
      const c = link.getChildByName(name);
      if (c) c.visible = this.available;
    }
    const status = link.getChildByName('online_status');
    if (status) {
      status.text = this.message ?? (this.available ? T.MSG_LOBBY_HINT : '');
      status.textColor = this.message ? ERROR_COLOR : HINT_COLOR;
    }
  }

  /** The list's slider (the task list's: shown for more rows than its mask shows, as new_interf init_instant does). */
  private layoutList(link: any, rows: number): void {
    const i = this.fwp._interf;
    const box = link.choose_container;
    const slider = link.slider;
    if (rows * listPitch(link.stage) <= LIST_ROWS * LIST_ROW) {
      slider.visible = false;
      box.y = LIST_TOP;
      return;
    }
    // new_interf.slider_proceed's formula for the fight screen's list (its top is 200 once the slider is used).
    if (!slider.visible) slider.y = i.slider_top;
    slider.visible = true;
    const p = Math.max(0, Math.min(1, (slider.y - i.slider_top) / (i.slider_bottom - i.slider_top)));
    box.y = 200 - p * (box.height - LIST_ROW - (i.slider_bottom - i.slider_top));
  }

  /** The picked room where a task shows its battle: its map's minimap and its teams (the players' names as typed). */
  private renderPreview(link: any, r: OnlineRoom | null): void {
    const i = this.fwp._interf;
    const bd = i.MainBitmapData;
    bd.fillRect(new Rectangle(0, 0, bd.width, bd.height), 0);
    if (r) {
      const mini = onlineMinimap(i, onlineMapOf(r.settings) ?? DEFAULT_ONLINE_MAP);
      bd.copyPixels(mini, new Rectangle(0, 0, 342, 154), new Point(10, 10));
      mini.dispose();
    }
    for (let t = 1; t <= 4; t++) {
      const ts = r ? r.settings.teams[t - 1] : undefined;
      const fields = ['team_name', 'attack', 'protect', 'health', 'worms'].map((n) => link[n + t]);
      if (!r || !ts) {
        for (const tf of fields) tf.text = '';
        continue;
      }
      const who = r.players.find((p) => p.team === t);
      const whoName = who ? playerName(who.displayName) : '';
      setRawTextFit(link['team_name' + t], who ? T.roomTeamPlayerText(whoName, who.playerId === r.hostId) : translate(T.MSG_FREE_SLOT), whoName);
      // An online battle's worms start without the career's bonuses (as the barracks' go_just builds them).
      link['attack' + t].text = '0';
      link['protect' + t].text = '0';
      link['health' + t].text = '100';
      link['worms' + t].text = String(ts.worms);
      for (const tf of fields) tf.textColor = i.game_get_color(ts.color);
      // The human flag (cell 0 of the CPU level flags) at a team a player plays.
      if (who) bd.copyPixels(i.CPUFlags, new Rectangle(0, 0, 19, 17), new Point(91, 173 + 20 * (t - 1)));
    }
  }

  private createRoom(isPrivate: boolean): void {
    if (this.busy) return;
    const settings = this.defaultSettings();
    this.busy = true;
    this.services.createRoom({ settings, private: isPrivate, maxPlayers: settings.teams.length }).then(
      (room: OnlineRoom) =>
        this.queue(() => {
          this.busy = false;
          this.log(`room ${room.roomId} created`);
          this.message = null; // a refusal shown in the lobby is not the room's
          this.adoptRoom(room);
          this.openRoomScreen();
        }),
      (err: any) => this.queue(() => this.failed(err)),
    );
  }

  /** A new room takes the player's own barracks (teams, worms, colours, names) on map 1. */
  private defaultSettings(): SentSettings {
    const b = this.fwp._interf.barracks;
    const teams: SentSettings['teams'] = [];
    let worms = 0;
    for (let t = 1; t <= Math.max(2, Math.min(4, Number(b.teams) || 2)); t++) {
      const bt = b['team' + t] ?? {};
      const w = Math.max(1, Math.min(8, Number(bt.worms) || 1, MAX_WORMS - worms - (Math.max(2, Number(b.teams) || 2) - t)));
      worms += w;
      teams.push({ worms: w, color: Number(bt.color) || t, name: String(bt.name || 'Команда #' + t).slice(0, 20) });
    }
    return { mapId: DEFAULT_ONLINE_MAP.mapId, teams };
  }

  private joinRoom(target: { roomId?: string; code?: string }): void {
    if (this.busy) return;
    this.busy = true;
    this.services.joinRoom(target).then(
      (room: OnlineRoom) =>
        this.queue(() => {
          this.busy = false;
          this.log(`joined room ${room.roomId}`);
          this.message = null; // a refusal shown in the lobby is not the room's
          this.adoptRoom(room);
          this.openRoomScreen();
        }),
      (err: any) => this.queue(() => this.failed(err)),
    );
  }

  /** A refused lobby request: a short message (the room screen also shows it). */
  private failed(err: any): void {
    this.busy = false;
    const code = codeOf(err);
    const msg = String(err?.message ?? '');
    // The backend services already renewed the session if they could: an UNAUTHORIZED here means it is over (expired,
    // ended from another device). As for every other request of the game: a clean restart into the sign-in screen.
    if (sessionEnded(this.fwp, err)) return;
    let text = T.MSG_ONLINE_FAILED;
    if (code === 'NOT_FOUND' || code === 'FORBIDDEN') text = T.MSG_ROOM_NOT_FOUND;
    else if (code === 'INVALID_STATE') text = /full/i.test(msg) ? T.MSG_ROOM_FULL : /lobby|running battle/i.test(msg) ? T.MSG_ROOM_IN_GAME : /disconnected/i.test(msg) ? T.MSG_WAIT_CONNECTED : /ready/i.test(msg) ? T.MSG_WAIT_READY : T.MSG_ONLINE_FAILED;
    this.log(`request failed: ${code} ${msg}`);
    this.message = text;
    this.notice(text);
    if (this.isRoomScreen() || this.isLobbyScreen()) this.fwp._interf.render();
  }

  // ------------------------------------------------------------------------------------------------ room screen

  /** The barracks screen in room mode (replaces the screen on top, like the fight screen's init_just_play). */
  private openRoomScreen(): void {
    const i = this.fwp._interf;
    if (!this.room) return;
    this.closePopups();
    if (i.cur_screen[0] == 0) i.init('mainframe');
    this.openingRoom = true;
    try {
      i.init('just_play', 'career');
    } finally {
      this.openingRoom = false;
    }
    this.roomLink = i.cur_screen[i.cur_screen[0]].link;
  }

  /** menu_just_play for the room (new_interf.render); false when the screen is the offline hot-seat barracks. */
  render_room(link: any): boolean {
    const r = this.room;
    if (!r || !(this.openingRoom || link === this.roomLink)) return false;
    this.roomLink = link;
    const i = this.fwp._interf;
    const s = r.settings;
    const host = r.hostId === this.playerId;
    const editable = host && r.state === 'lobby';
    // The map: every PvP map for the host to pick from, the room's map only for the others; its minimap.
    this.maps.render(link, onlineMapOf(s) ?? DEFAULT_ONLINE_MAP, editable);
    const n = s.teams.length;
    const total = s.teams.reduce((a, t) => a + t.worms, 0);
    link.teams.text = n;
    link.add_team.visible = editable && n < 4 && total < MAX_WORMS;
    link.remove_team.visible = editable && n > 2 && !r.players.some((p) => p.team === n);
    for (let t = 1; t <= 4; t++) {
      const ts = s.teams[t - 1];
      link['team' + t].visible = t <= n;
      link['worms' + t].visible = t <= n;
      link['remove_worm' + t].visible = editable && !!ts && ts.worms > 1;
      link['add_worm' + t].visible = editable && !!ts && ts.worms < 8 && total < MAX_WORMS;
      if (!ts) continue;
      const who = r.players.find((p) => p.team === t) ?? r.players.find((p) => p.playerId === r.hostId);
      const whoName = who ? playerName(who.displayName) : null;
      setRawTextFit(link['team' + t], T.roomTeamText(ts.name, whoName), whoName ?? '');
      link['worms' + t].text = ts.worms;
      link['team' + t].textColor = i.game_get_color(ts.color);
      link['worms' + t].textColor = i.game_get_color(ts.color);
    }
    this.roomModes(link);
    const info = textAt(link, JUST_PLAY_INFO);
    if (info) {
      // Display text: the game's lines translated one by one, the players' names as typed (never translated).
      const lines: string[] = [r.private && r.code ? translate(T.MSG_ROOM_CODE) + r.code : translate(T.MSG_PUBLIC_ROOM)];
      for (const p of r.players) lines.push(T.roomPlayerText(playerName(p.displayName), p.playerId === r.hostId, p.ready, p.connected));
      lines.push(translate(host ? T.MSG_HOST_HINT : T.MSG_GUEST_HINT));
      if (this.message) lines.push(translate(this.message));
      // The offline description broke its lines by hand (<sbr />); the room's lines wrap in the field's width.
      info.wordWrap = true;
      setRawText(info, lines.join('\n'));
    }
    return true;
  }

  /**
   * The mode board of a room: "Онлайн" is its mode (the button hides, its tinted card sel_online shows, as the fight
   * screen shows a chosen mode). The other modes stay on the board dimmed and do not react: they lead away from the
   * room, the close button leaves it. The tinted copies that mark another mode as chosen (the TV of "Просто играть",
   * sel_just; the boss card under its button, sel_boss) are not the room's: the TV shows as the other cards.
   */
  private roomModes(link: any): void {
    for (const name of ['career', 'missions', 'survival', 'boss']) {
      const b = link[name];
      if (!b) continue;
      b.alpha = OTHER_MODE_ALPHA;
      b.mouseEnabled = false;
      b.enabled = false;
    }
    if (link.online) link.online.visible = false;
    if (link.sel_boss) link.sel_boss.visible = false;
    if (link.sel_just) {
      link.sel_just.transform.colorTransform = new ColorTransform();
      link.sel_just.alpha = OTHER_MODE_ALPHA;
    }
  }

  private editSettings(change: (s: SentSettings) => boolean): void {
    const r = this.room;
    if (!r || r.hostId !== this.playerId || r.state !== 'lobby' || this.busy) return;
    const s: SentSettings = { mapId: (onlineMapOf(r.settings) ?? DEFAULT_ONLINE_MAP).mapId, teams: r.settings.teams.map((t) => ({ ...t })) };
    if (!change(s)) return;
    this.busy = true;
    this.services.updateRoomSettings(r.roomId, s).then(
      (room: OnlineRoom) =>
        this.queue(() => {
          this.busy = false;
          this.adoptRoom(room);
        }),
      (err: any) => this.queue(() => this.failed(err)),
    );
  }

  /** go_just in the room: Ready for a guest; for the host, once everyone is ready: ready + room.start. */
  private goJust(): void {
    const r = this.room;
    if (!r || r.state !== 'lobby' || this.busy) return;
    const me = r.players.find((p) => p.playerId === this.playerId);
    if (!me) return;
    const services = this.services;
    if (r.hostId !== this.playerId) {
      this.busy = true;
      services.setReady(r.roomId, !me.ready).then(
        (room: OnlineRoom) =>
          this.queue(() => {
            this.busy = false;
            this.adoptRoom(room);
          }),
        (err: any) => this.queue(() => this.failed(err)),
      );
      return;
    }
    let why: string | null = null;
    if (r.players.length < 2) why = T.MSG_WAIT_PLAYERS;
    else if (!r.players.every((p) => p.playerId === r.hostId || p.ready)) why = T.MSG_WAIT_READY;
    else if (!r.players.every((p) => p.connected)) why = T.MSG_WAIT_CONNECTED;
    if (why) {
      this.message = why;
      this.fwp._interf.render();
      return;
    }
    this.busy = true;
    this.message = T.MSG_STARTING;
    this.fwp._interf.render();
    (me.ready ? Promise.resolve(null) : services.setReady(r.roomId, true)).then(() => services.startRoom(r.roomId)).then(
      (session: OnlineSession) =>
        this.queue(() => {
          this.busy = false;
          this.onSessionStarted(session);
        }),
      (err: any) => this.queue(() => this.failed(err)),
    );
  }

  private leaveRoom(): void {
    const r = this.room;
    if (!r) return this.openLobbyScreen();
    if (this.busy) return;
    this.busy = true;
    this.services.leaveRoom(r.roomId).then(
      () =>
        this.queue(() => {
          this.busy = false;
          this.log(`left room ${r.roomId}`);
          this.lostRoom(null);
          if (!this.isLobbyScreen()) this.openLobbyScreen();
        }),
      (err: any) =>
        this.queue(() => {
          if (codeOf(err) === 'NOT_FOUND' || codeOf(err) === 'FORBIDDEN') {
            this.busy = false;
            this.lostRoom(null);
            if (!this.isLobbyScreen()) this.openLobbyScreen();
          } else this.failed(err);
        }),
    );
  }

  /** Joins the private room whose code the lobby's field holds (its button, or Enter in the field). */
  private joinByCode(link: any): void {
    const code = String(link.getChildByName('online_code')?.text ?? '')
      .trim()
      .toUpperCase();
    if (!code) this.notice(T.MSG_ENTER_CODE);
    // A room code is 4..12 letters and digits (room.join): anything else cannot be a room.
    else if (!/^[A-Z0-9]{4,12}$/.test(code)) this.notice(T.MSG_ROOM_NOT_FOUND);
    else this.joinRoom({ code });
  }

  // ------------------------------------------------------------------------------------------------ clicks

  /** new_interf.click: the lobby's, the room's, the result's and the online battle's controls. True = handled. */
  click(e: any): boolean {
    const target = e && e.target;
    const name = String(target?.name ?? '');
    const f = this.fwp;
    // The surrender button of an online battle: the player leaves the session (session.forfeit) in any turn, so an
    // opponent that keeps its turn cannot lock the player into the battle.
    if (name == 'cancel_fight' && this.match && this.match.owns(f._proc)) {
      this.match.forfeit();
      return true;
    }
    if (this.isResultScreen()) {
      if (name == 'proceed' || name == 'close') {
        this.closeResult();
        return true;
      }
      return false;
    }
    if (this.isLobbyScreen()) {
      const link = this.top()!.link;
      if (name == 'online_create') this.createRoom(false);
      else if (name == 'online_create_private') this.createRoom(true);
      else if (name == 'online_join_code') this.joinByCode(link);
      else if (name == 'go') {
        // The battle button of the list: join the picked room.
        const r = this.selectedRoom();
        if (r && !roomFull(r)) this.joinRoom({ roomId: r.roomId });
      } else if (name == 'choose_task_button' || name == 'click_task') {
        // A row of the list (named by what it is, the original's rows by their task number).
        const row = String(target.parent?.name ?? '');
        if (row == 'online_more') this.loadRooms(false);
        else if (row.startsWith('room_')) {
          const r = this.rooms[Number(row.slice('room_'.length))];
          if (r && !roomFull(r)) {
            this.selectedRoomId = r.roomId;
            this.playerPicked = true;
            this.message = null;
            f._interf.render();
          }
        }
      } else if (name != 'online_code') return false;
      return true;
    }
    if (this.isRoomScreen()) {
      if (name == 'go_just') this.goJust();
      else if (name == 'close_to_mf') this.leaveRoom();
      else if (name == 'add_team')
        this.editSettings((s) => {
          const total = s.teams.reduce((a, t) => a + t.worms, 0);
          if (s.teams.length >= 4 || total >= MAX_WORMS) return false;
          const k = s.teams.length + 1;
          const bt = f._interf.barracks['team' + k] ?? {};
          s.teams.push({ worms: 1, color: Number(bt.color) || k, name: 'Команда #' + k });
          return true;
        });
      else if (name == 'remove_team') this.editSettings((s) => s.teams.length > 2 && !this.room!.players.some((p) => p.team === s.teams.length) && !!s.teams.pop());
      else if (name.startsWith('add_worm') || name.startsWith('remove_worm')) {
        const add = name.startsWith('add_worm');
        const t = Number(name.slice(add ? 'add_worm'.length : 'remove_worm'.length));
        this.editSettings((s) => {
          const ts = s.teams[t - 1];
          const total = s.teams.reduce((a, x) => a + x.worms, 0);
          if (!ts || (add ? ts.worms >= 8 || total >= MAX_WORMS : ts.worms <= 1)) return false;
          ts.worms += add ? 1 : -1;
          return true;
        });
      } else if (name == 'choose_task_button' || name == 'click_task') {
        // An entry of the map list (named by the map's number).
        const map = onlineMap(Number(target.parent?.name));
        this.editSettings((s) => {
          if (!map || map.mapId === s.mapId) return false;
          s.mapId = map.mapId;
          return true;
        });
      } else if (name == 'career' || name == 'missions' || name == 'survival' || name == 'boss' || name == 'online') {
        /* not available in the room (roomModes) */
      } else return false;
      return true;
    }
    return false;
  }
}
